import { ROUTES } from 'module/navigation/enums/routes.enums';
import PreloadComp from 'module/preload/PreloadComp';
import { useEffect, useRef, useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import HeaderBar from '../HeaderBar/HeaderBar';
import MabelSidebar from '../MabelSideBar/MabelSidebar';
import { DocumentProvider } from 'module/documents/context';
import { useWorkplaceUser } from 'module/documents/utils';
import { SessionGate } from 'module/auth/SessionGate';
import Manager from '../../../module/schedule/Manager';
import EmployeeOnboardingProgress from '../../../module/onboarding/page/EmployeeOnboardingProgress';
import styles from './Layout.module.scss';

/**
 * Root application layout component.
 *
 * Handles two rendering paths:
 * - **Onboarding in progress**: Renders only the {@link EmployeeOnboardingProgress} flow
 *   when `user.isOnboardingInProgress` is `true`, hiding all navigation.
 * - **Standard layout**: Renders the full shell with {@link HeaderBar}, {@link MabelSidebar},
 *   and the main content area with all route definitions.
 *
 * Shows `null` while the current user data is loading to avoid a flash of incorrect layout.
 */
function LayoutContent({ user }: { user: NonNullable<ReturnType<typeof useWorkplaceUser>['data']> }) {
	const mainContentRef = useRef<HTMLElement>(null);
	const location = useLocation();
	const [newEmployee, setNewEmployee] = useState({
		firstName: '',
		middleName: '',
		lastName: '',
		email: '',
	});

	useEffect(() => {
		mainContentRef.current?.scrollTo(0, 0);
	}, [location.pathname]);

	const isSettingsRoute = location.pathname.startsWith(ROUTES.SETTINGS);

	return (
		<div className={styles.appShell}>
			<DocumentProvider
				defaultUser={user}
				newEmployeeState={[newEmployee, setNewEmployee]}
			>
				<MabelSidebar />
				<div className={styles.mainArea}>
					<HeaderBar />
					<main
						ref={mainContentRef}
						className={`${styles.mainContent}${isSettingsRoute ? ` ${styles.mainContentContained}` : ''}`}
					>
						<Switch>
							<Route path={ROUTES.MANAGER} component={Manager} />
							<Redirect from="/manager/dashboard" to={ROUTES.HOMEPAGE} />
							<Redirect from="/" to={ROUTES.HOMEPAGE} />
						</Switch>
					</main>
				</div>
			</DocumentProvider>
		</div>
	);
}

function GatedLayout(): JSX.Element {
	const { data: user, isLoading } = useWorkplaceUser();

	if (isLoading || !user) {
		return null;
	}

	// Check if user is in onboarding progress
	const isOnboardingInProgress = user?.isOnboardingInProgress === true;

	// If user is in onboarding, show only the onboarding flow
	if (isOnboardingInProgress) {
		return (
			<PreloadComp>
				<EmployeeOnboardingProgress />
			</PreloadComp>
		);
	}

	return (
		<PreloadComp>
			<LayoutContent user={user} />
		</PreloadComp>
	);
}

/**
 * SEC-014: nothing in the shell (including its data queries) mounts until the
 * post-login steps are complete. See {@link SessionGate}.
 */
export default function Layout(): JSX.Element {
	return (
		<SessionGate>
			<GatedLayout />
		</SessionGate>
	);
}
